import Vue from 'vue';

import { cloneDeep } from 'lodash';
import UserApi from 'client/api/v2/UserApi';
import createModel from 'client/store/modules/models/createModel';

export const getDefaultItem = () => ({
  _id: '',
  email: '',
  enableMultiFactor: false,
  firstname: '',
  lastname: '',
  administratorOrganizations: [],
  registered: false,
  role: '',
  suspended: false,
});

/**
 * The ProviderModel Vuex module provides an interface to the Provider
 * item being created or updated in the Admin List view.
 */
export function createAdministratorModel(userApi = UserApi) {
  const model = createModel(getDefaultItem, '_id', userApi);

  return {
    namespaced: true,

    state: {
      ...model.state,
      refreshKey: 0,
      resetKey: 0,
      mdmVisibility: true,
    },

    actions: {
      ...model.actions,

      async resetActionKey(context) {
        context.commit('UPDATE_RESET_KEY');
      },

      async resetMdm(context, visible) {
        context.commit('UPDATE_MDM_VISIBILITY', visible);
      },

      async deleteItem(context, itemId) {
        await userApi.delete(itemId);
        context.commit('UPDATE_REFRESH_KEY');
      },

      async fetchItem(context, itemId) {
        if (itemId != null) {
          context.dispatch('updateItem', { _id: itemId });
          return model.actions.fetchItem(context);
        }
        throw new Error('No itemId');
      },

      async resendEmail(context, id) {
        try {
          await userApi.resendEmail(id);
          return true;
        } catch (e) {
          // return the exception if you'd like...
          return false;
        }
      },

      async saveItem(context) {
        const { getters } = context;

        const handleSaveItem = async (item) => {
          context.commit('SET_ITEM', cloneDeep(item));
          context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
          context.commit('SET_ITEM_STATE', { update: true });
          context.commit('UPDATE_REFRESH_KEY');

          // are we updating the logged in user? If so, reload them to reflect possible role changes
          if (context.rootGetters['SettingsModule/getUser'].id === (item._id || item.id)) {
            // Wait for settings refresh so the UI is updated with latest settings
            await context.dispatch('SettingsModule/fetchUserSettings', undefined, { root: true });
          }

          return item;
        };

        return getters.isItemBeingCreated
          ? userApi.create(getters.item).then(handleSaveItem)
          : userApi.update(getters.item).then(handleSaveItem);
      },
    },

    getters: {
      ...model.getters,

      displayName(state) {
        return state.item.email || '';
      },

      /**
       * Legacy items that reside in Mongo use the _id field while newer items
       * that live in Postgres, such as Policies and Groups, use the id field.
       */
      idField() {
        return '_id';
      },

      // Mapping roleName so that we don't need to update both role and roleName
      item(state, getters, rootState) {
        const baseItem = model.getters.item(state);
        const { roles } = rootState.SettingsModule;

        if (roles != null && baseItem.role != null) {
          const permissions = roles
            .map((role) => ({ name: role.name, value: role._id }))
            .sort((item1, item2) => (item1.name > item2.name ? 1 : -1));

          const roleName = permissions
            .find((permission) => permission.value === baseItem.role).name;

          return {
            ...baseItem,
            roleName,
          };
        }

        return baseItem;
      },
    },

    mutations: {
      ...model.mutations,
      UPDATE_REFRESH_KEY(state) {
        Vue.set(state, 'refreshKey', state.refreshKey + 1);
      },

      UPDATE_RESET_KEY(state) {
        Vue.set(state, 'resetKey', state.resetKey + 1);
      },

      UPDATE_MDM_VISIBILITY(state, visible) {
        Vue.set(state, 'mdmVisibility', visible);
      },
    },
  };
}

export default createAdministratorModel();
